import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Wrench, Plus, Trash2, Save, Pencil } from 'lucide-react';
import { listTools, createTool, updateTool, deleteTool } from '@/lib/api';
import { toast } from 'sonner';
import { IDS } from '@/constants/testIds/wizard';

const emptyTool = () => ({
  number: 1, name: 'Vegmaro', type: 'endmill', diameter: 6, flute_length: 20,
  flutes: 2, max_rpm: 24000, material: 'HSS', notes: '',
});

const TYPE_HU = {
  endmill: 'Vegmaro', drill: 'Furoszal', vbit: 'V-maro', ballnose: 'Gomb', thread: 'Menetmaro', face: 'Homlokmaro',
};

export const ToolsDialog = ({ open, onOpenChange }) => {
  const [tools, setTools] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyTool());
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      const t = await listTools();
      setTools(t);
    } catch (e) { toast.error('Szerszamok betoltese sikertelen'); }
  };
  useEffect(() => { if (open) load(); }, [open]);

  const startNew = () => { setEditing(null); setForm(emptyTool()); };
  const startEdit = (t) => { setEditing(t.id); setForm({ ...t }); };

  const save = async () => {
    setLoading(true);
    try {
      if (editing) await updateTool(editing, form);
      else await createTool(form);
      toast.success('Szerszam mentve');
      await load();
      startNew();
    } catch (e) { toast.error('Mentesi hiba'); }
    finally { setLoading(false); }
  };
  const remove = async (id) => {
    try { await deleteTool(id); await load(); toast.success('Torolve'); }
    catch { toast.error('Torles sikertelen'); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl bg-card border-border">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Wrench className="h-4 w-4 text-primary" /> Szerszam adatbazis</DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">Szerszam adatbazis kezelese.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-muted-foreground">{tools.length} szerszam</span>
              <Button size="sm" variant="secondary" onClick={startNew} data-testid={IDS.tools.add}><Plus className="h-3.5 w-3.5 mr-1" />Uj</Button>
            </div>
            <div className="max-h-96 overflow-y-auto" data-testid={IDS.tools.table}>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">T#</TableHead>
                    <TableHead>Nev</TableHead>
                    <TableHead>Tipus</TableHead>
                    <TableHead className="text-right">D</TableHead>
                    <TableHead className="text-right">Elek</TableHead>
                    <TableHead className="w-20 text-right">M.</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tools.map(t => (
                    <TableRow key={t.id} className="hover:bg-[rgba(0,255,65,0.04)] cursor-pointer" onClick={() => startEdit(t)}>
                      <TableCell className="tabular">T{t.number}</TableCell>
                      <TableCell>{t.name}</TableCell>
                      <TableCell className="text-xs">{TYPE_HU[t.type] || t.type}</TableCell>
                      <TableCell className="text-right tabular">{t.diameter}</TableCell>
                      <TableCell className="text-right tabular">{t.flutes}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-1 justify-end">
                          <Button size="icon" variant="ghost" onClick={(e) => { e.stopPropagation(); startEdit(t); }}><Pencil className="h-3.5 w-3.5" /></Button>
                          <Button size="icon" variant="ghost" onClick={(e) => { e.stopPropagation(); remove(t.id); }} data-testid={IDS.tools.delete}><Trash2 className="h-3.5 w-3.5 text-red-400" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {tools.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground text-xs">Nincs szerszam. Adj hozza!</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </div>
          <div className="space-y-2">
            <h3 className="text-sm font-semibold">{editing ? 'Szerszam szerkesztese' : 'Uj szerszam'}</h3>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Sorszam (T)</Label><Input type="number" value={form.number} onChange={e => setForm({ ...form, number: parseInt(e.target.value || '1', 10) })} /></div>
              <div><Label className="text-xs">Nev</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
              <div>
                <Label className="text-xs">Tipus</Label>
                <Select value={form.type} onValueChange={v => setForm({ ...form, type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(TYPE_HU).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Anyag</Label>
                <Select value={form.material} onValueChange={v => setForm({ ...form, material: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="HSS">HSS</SelectItem>
                    <SelectItem value="cobalt">Kobalt</SelectItem>
                    <SelectItem value="carbide">Kemenyfem</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Atmero (mm)</Label><Input type="number" step="0.1" value={form.diameter} onChange={e => setForm({ ...form, diameter: parseFloat(e.target.value) })} /></div>
              <div><Label className="text-xs">Elhossz (mm)</Label><Input type="number" step="0.5" value={form.flute_length} onChange={e => setForm({ ...form, flute_length: parseFloat(e.target.value) })} /></div>
              <div><Label className="text-xs">Elek szama</Label><Input type="number" value={form.flutes} onChange={e => setForm({ ...form, flutes: parseInt(e.target.value || '1', 10) })} /></div>
              <div><Label className="text-xs">Max RPM</Label><Input type="number" step="500" value={form.max_rpm} onChange={e => setForm({ ...form, max_rpm: parseInt(e.target.value || '1000', 10) })} /></div>
            </div>
            <div><Label className="text-xs">Megjegyzes</Label><Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="secondary" onClick={startNew}>Megse</Button>
              <Button onClick={save} disabled={loading} data-testid={IDS.tools.save}><Save className="h-4 w-4 mr-1" /> {editing ? 'Frissites' : 'Hozzaadas'}</Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ToolsDialog;
